import { Layout } from "@/components/layout/layout";
import { useCurrentPlayer, usePlayerAuthActions } from "@/hooks/useCurrentPlayer";
import { useLocation, Link } from "wouter";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  useGetPlayerHistory,
  getGetPlayerHistoryQueryKey,
  useGetPlayerAttendance,
  getGetPlayerAttendanceQueryKey,
  useListMyRegistrations,
  useCancelMyRegistration,
  getListMyRegistrationsQueryKey,
  useUpdateMyProfile,
  useUpdateMyAccount,
  useChangeMyPassword,
  useRequestMyEmailChange,
  useSetMyAvatar,
  getGetPlayerMeQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, LogOut, Trophy, History, Calendar, X, Eye, EyeOff,
  ExternalLink, Bell, BellOff, Camera, User, Mail, KeyRound, BarChart3,
  CheckCircle2,
} from "lucide-react";
import { formatDateTime, formatMoney } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useQueryClient } from "@tanstack/react-query";
import { uploadImage, storedImageUrl } from "@/lib/upload";
import { AttendanceHeatmap } from "@/components/attendance-heatmap";

function parseError(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    try {
      const parsed = JSON.parse(err.message) as { error?: string };
      if (parsed?.error) return parsed.error;
    } catch {
      // not JSON
    }
    return err.message || fallback;
  }
  return fallback;
}

export default function Me() {
  const { player, isLoggedIn, isLoading, setPlayer } = useCurrentPlayer();
  const { logout } = usePlayerAuthActions();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const myRegistrationsKey = getListMyRegistrationsQueryKey();
  const { data: upcoming, isLoading: isUpcomingLoading } = useListMyRegistrations({
    query: { enabled: !!player, queryKey: myRegistrationsKey },
  });

  const updateProfile = useUpdateMyProfile({
    mutation: {
      onSuccess: (updated, variables) => {
        setPlayer(updated);
        queryClient.setQueryData(getGetPlayerMeQueryKey(), updated);
        const data = (variables?.data ?? {}) as {
          remindersOptOut?: boolean;
          isPublicProfile?: boolean;
          displayName?: string | null;
          showName?: boolean;
          showEmail?: boolean;
          showPhone?: boolean;
          publicAttendance?: boolean;
        };
        if (data.remindersOptOut !== undefined) {
          toast({
            title: updated.remindersOptOut ? "Email reminders off" : "Email reminders on",
            description: updated.remindersOptOut
              ? "We won't email you 24h or 1h before tournaments you've RSVP'd to."
              : "You'll get an email 24h and 1h before tournaments you've RSVP'd to.",
          });
        } else if (data.isPublicProfile !== undefined) {
          toast({
            title: updated.isPublicProfile ? "Public profile on" : "Public profile off",
            description: updated.isPublicProfile
              ? "Your /players/<id> page is now visible to anyone."
              : "Your /players/<id> page is hidden.",
          });
        } else if (data.showName !== undefined) {
          toast({
            title: updated.showName ? "Name shown on rosters" : "Name hidden on rosters",
          });
        } else if (data.showEmail !== undefined) {
          toast({
            title: updated.showEmail ? "Email shown on rosters" : "Email hidden on rosters",
          });
        } else if (data.showPhone !== undefined) {
          toast({
            title: updated.showPhone ? "Phone shown on rosters" : "Phone hidden on rosters",
          });
        } else if (data.publicAttendance !== undefined) {
          toast({
            title: updated.publicAttendance
              ? "Attendance heatmap shown"
              : "Attendance heatmap hidden",
            description: updated.publicAttendance
              ? "Visitors to your public profile can see when you've played."
              : "Your day-by-day attendance is now hidden on your public profile. You'll still see it here on /me.",
          });
        } else if (data.displayName !== undefined) {
          toast({
            title: updated.displayName
              ? "Display name saved"
              : "Display name cleared",
            description: updated.displayName
              ? `You'll appear as "${updated.displayName}" on public rosters.`
              : "You'll appear under your account name on public rosters.",
          });
        }
      },
      onError: () => {
        toast({ title: "Could not update profile", variant: "destructive" });
      },
    },
  });

  const cancelMutation = useCancelMyRegistration({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: myRegistrationsKey });
        toast({ title: "RSVP cancelled" });
      },
      onError: (err: Error) => {
        toast({
          title: "Cancellation failed",
          description: parseError(err, "Could not cancel your RSVP."),
          variant: "destructive",
        });
      },
    },
  });

  useEffect(() => {
    if (!isLoading && !isLoggedIn) {
      setLocation("/login");
    }
  }, [isLoading, isLoggedIn, setLocation]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const setAvatar = useSetMyAvatar({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetPlayerMeQueryKey() });
        toast({ title: "Avatar updated" });
      },
      onError: () => {
        toast({ title: "Avatar update failed", variant: "destructive" });
      },
    },
  });

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploadingAvatar(true);
    try {
      const { objectPath } = await uploadImage(file);
      await setAvatar.mutateAsync({ data: { avatarUrl: objectPath } });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Could not upload avatar.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // ---- Profile (name / phone) form ----
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [displayName, setDisplayName] = useState("");
  useEffect(() => {
    if (player) {
      setName(player.name);
      setPhone(player.phone ?? "");
      setDisplayName(player.displayName ?? "");
    }
  }, [player?.id, player?.name, player?.phone]);

  const updateAccount = useUpdateMyAccount({
    mutation: {
      onSuccess: (updated) => {
        setPlayer(updated);
        queryClient.setQueryData(getGetPlayerMeQueryKey(), updated);
        toast({ title: "Profile saved" });
      },
      onError: (err: Error) => {
        toast({
          title: "Could not save profile",
          description: parseError(err, "Please try again."),
          variant: "destructive",
        });
      },
    },
  });

  const profileDirty =
    !!player &&
    (name.trim() !== player.name || (phone.trim() || null) !== (player.phone ?? null));

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileDirty) return;
    updateAccount.mutate({
      data: { name: name.trim(), phone: phone.trim() ? phone.trim() : null },
    });
  };

  // ---- Change password form ----
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const changePassword = useChangeMyPassword({
    mutation: {
      onSuccess: () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        toast({ title: "Password updated" });
      },
      onError: (err: Error) => {
        toast({
          title: "Could not change password",
          description: parseError(err, "Please try again."),
          variant: "destructive",
        });
      },
    },
  });

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Use at least 8 characters.",
        variant: "destructive",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "The new password and confirmation must be the same.",
        variant: "destructive",
      });
      return;
    }
    changePassword.mutate({ data: { currentPassword, newPassword } });
  };

  // ---- Email change form ----
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const requestEmailChange = useRequestMyEmailChange({
    mutation: {
      onSuccess: () => {
        setNewEmail("");
        setEmailPassword("");
        queryClient.invalidateQueries({ queryKey: getGetPlayerMeQueryKey() });
        toast({
          title: "Check your new inbox",
          description: "We sent a link to confirm the change. Your email won't change until you click it.",
        });
      },
      onError: (err: Error) => {
        toast({
          title: "Could not request email change",
          description: parseError(err, "Please try again."),
          variant: "destructive",
        });
      },
    },
  });

  const handleRequestEmailChange = (e: React.FormEvent) => {
    e.preventDefault();
    requestEmailChange.mutate({
      data: { newEmail: newEmail.trim(), currentPassword: emailPassword },
    });
  };

  const playerAvatarUrl = storedImageUrl(player?.avatarUrl ?? null);
  const playerId = player?.id || 0;
  const { data: history, isLoading: isHistoryLoading } = useGetPlayerHistory(playerId, {
    query: { enabled: !!player?.id, queryKey: getGetPlayerHistoryQueryKey(playerId) }
  });
  const { data: attendance } = useGetPlayerAttendance(playerId, undefined, {
    query: { enabled: !!player?.id, queryKey: getGetPlayerAttendanceQueryKey(playerId) }
  });

  const lifetimeStats = useMemo(() => {
    if (!history || history.length === 0) {
      return { played: 0, cashes: 0, itmPct: 0, biggestWin: 0, totalWinnings: 0 };
    }
    let cashes = 0;
    let biggestWin = 0;
    let totalWinnings = 0;
    for (const h of history) {
      if (h.winnings > 0) {
        cashes += 1;
        totalWinnings += h.winnings;
        if (h.winnings > biggestWin) biggestWin = h.winnings;
      }
    }
    const played = history.length;
    return {
      played,
      cashes,
      itmPct: played > 0 ? Math.round((cashes / played) * 1000) / 10 : 0,
      biggestWin,
      totalWinnings,
    };
  }, [history]);

  // Newly-promoted-off-the-waitlist banners. Show for any active RSVP that
  // has a promotedAt timestamp within the last 14 days, until the player
  // dismisses it (per-registration, persisted in localStorage).
  const promotedDismissKey = player?.id
    ? `icp.promotedDismissed.${player.id}`
    : null;
  const [promotedDismissed, setPromotedDismissed] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (!promotedDismissKey || typeof window === "undefined") {
      setPromotedDismissed(new Set());
      return;
    }
    try {
      const raw = window.localStorage.getItem(promotedDismissKey);
      if (!raw) {
        setPromotedDismissed(new Set());
        return;
      }
      const arr = JSON.parse(raw) as unknown;
      if (!Array.isArray(arr)) {
        setPromotedDismissed(new Set());
        return;
      }
      setPromotedDismissed(new Set(arr.filter((n): n is number => typeof n === "number")));
    } catch {
      setPromotedDismissed(new Set());
    }
  }, [promotedDismissKey]);
  const dismissPromoted = (tournamentId: number) => {
    if (!promotedDismissKey) return;
    setPromotedDismissed((prev) => {
      const next = new Set(prev);
      next.add(tournamentId);
      try {
        window.localStorage.setItem(promotedDismissKey, JSON.stringify([...next]));
      } catch {
        // best-effort
      }
      return next;
    });
  };
  const promotedBanners = useMemo(() => {
    if (!upcoming) return [];
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return upcoming.filter((r) => {
      if (r.registrationStatus !== "registered") return false;
      if (!r.promotedAt) return false;
      const t = new Date(r.promotedAt).getTime();
      if (!Number.isFinite(t) || t < cutoff) return false;
      return !promotedDismissed.has(r.tournamentId);
    });
  }, [upcoming, promotedDismissed]);

  if (!player) return null;

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-end justify-between gap-6">
            <div className="flex items-center gap-6 text-center sm:text-left">
              <div className="relative">
                {playerAvatarUrl ? (
                  <img
                    src={playerAvatarUrl}
                    alt={player.name}
                    className="h-24 w-24 rounded-full object-cover border-2 border-secondary/30"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold text-4xl border-2 border-secondary/30">
                    {player.name.charAt(0)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingAvatar || setAvatar.isPending}
                  className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 disabled:opacity-50"
                  data-testid="button-upload-avatar"
                  aria-label="Upload avatar"
                >
                  {isUploadingAvatar || setAvatar.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                  data-testid="input-avatar-file"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">{player.name}</h1>
                <p className="text-muted-foreground mt-1">{player.email}</p>
                {player.pendingEmail ? (
                  <p className="text-xs text-primary mt-1" data-testid="text-pending-email">
                    Pending verification: {player.pendingEmail}
                  </p>
                ) : null}
              </div>
            </div>
            <Button
              variant="outline"
              className="text-muted-foreground border-border hover:bg-destructive/10 hover:text-destructive"
              disabled={logout.isPending}
              onClick={() => {
                logout.mutate(undefined, {
                  onSuccess: () => {
                    toast({ title: "Signed out" });
                    setLocation("/login");
                  },
                });
              }}
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-5xl space-y-8">
        {/* Lifetime stats */}
        <Card className="bg-background border-border shadow-md" data-testid="card-lifetime-stats">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-primary" /> Lifetime Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Played</p>
                <p className="text-2xl font-bold text-foreground" data-testid="stat-played">{lifetimeStats.played}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Cashes</p>
                <p className="text-2xl font-bold text-primary" data-testid="stat-cashes">{lifetimeStats.cashes}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">ITM %</p>
                <p className="text-2xl font-bold text-foreground" data-testid="stat-itm">
                  {lifetimeStats.played > 0 ? `${lifetimeStats.itmPct}%` : "—"}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Biggest Win</p>
                <p className="text-2xl font-bold text-green-500" data-testid="stat-biggest">
                  {lifetimeStats.biggestWin > 0 ? formatMoney(lifetimeStats.biggestWin) : "—"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Attendance heatmap */}
        <Card className="bg-background border-border shadow-md" data-testid="card-attendance">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" /> Attendance (last 12 months)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <AttendanceHeatmap
              cells={(attendance ?? []).map((d) => ({
                date: d.date,
                count: d.tournamentCount,
                href: `/tournaments/${d.tournamentId}`,
                tooltip: `${d.date}: ${d.tournamentName} (${d.leagueName})${d.tournamentCount > 1 ? ` +${d.tournamentCount - 1} more` : ""}`,
              }))}
              emptyLabel="No tournaments attended in the last 12 months."
            />
          </CardContent>
        </Card>

        {/* Profile (name / phone) */}
        <Card className="bg-background border-border shadow-md" data-testid="card-profile-edit">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <User className="h-6 w-6 text-primary" /> Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSaveProfile} className="space-y-4 max-w-lg">
              <div className="space-y-2">
                <Label htmlFor="profile-name">Name</Label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={120}
                  data-testid="input-profile-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-phone">Phone (optional)</Label>
                <Input
                  id="profile-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  type="tel"
                  maxLength={40}
                  placeholder="+1 555-555-5555"
                  data-testid="input-profile-phone"
                />
              </div>
              <Button
                type="submit"
                disabled={!profileDirty || updateAccount.isPending}
                className="bg-primary text-primary-foreground"
                data-testid="button-save-profile"
              >
                {updateAccount.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save Changes
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Email change */}
        <Card className="bg-background border-border shadow-md" data-testid="card-email-change">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" /> Change Email
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-4 max-w-xl">
              For security, we'll email a confirmation link to your new address. Your email
              only changes once you open that link. You'll be signed out everywhere after
              confirming.
            </p>
            {player.pendingEmail ? (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3 mb-4 text-sm">
                A change to <strong>{player.pendingEmail}</strong> is awaiting verification.
                Check that inbox for the confirmation link.
              </div>
            ) : null}
            <form onSubmit={handleRequestEmailChange} className="space-y-4 max-w-lg">
              <div className="space-y-2">
                <Label htmlFor="email-new">New Email</Label>
                <Input
                  id="email-new"
                  type="email"
                  required
                  maxLength={255}
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="input-new-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-current-pw">Current Password</Label>
                <Input
                  id="email-current-pw"
                  type="password"
                  required
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  data-testid="input-email-current-password"
                />
              </div>
              <Button
                type="submit"
                disabled={requestEmailChange.isPending || !newEmail || !emailPassword || !player.passwordSet}
                className="bg-primary text-primary-foreground"
                data-testid="button-request-email-change"
              >
                {requestEmailChange.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Send Verification Link
              </Button>
              {!player.passwordSet ? (
                <p className="text-xs text-muted-foreground">
                  Set a password first to enable email changes.
                </p>
              ) : null}
            </form>
          </CardContent>
        </Card>

        {/* Change password */}
        <Card className="bg-background border-border shadow-md" data-testid="card-change-password">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <KeyRound className="h-6 w-6 text-primary" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {player.passwordSet ? (
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-lg">
                <div className="space-y-2">
                  <Label htmlFor="pw-current">Current Password</Label>
                  <Input
                    id="pw-current"
                    type="password"
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    data-testid="input-current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pw-new">New Password</Label>
                  <Input
                    id="pw-new"
                    type="password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="input-new-password"
                  />
                  <p className="text-xs text-muted-foreground">At least 8 characters.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pw-confirm">Confirm New Password</Label>
                  <Input
                    id="pw-confirm"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="input-confirm-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={changePassword.isPending || !currentPassword || !newPassword || !confirmPassword}
                  className="bg-primary text-primary-foreground"
                  data-testid="button-change-password"
                >
                  {changePassword.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Update Password
                </Button>
              </form>
            ) : (
              <div className="text-sm text-muted-foreground">
                Your account doesn't have a password yet.{" "}
                <Link href="/login" className="text-primary hover:underline">
                  Set one by email
                </Link>{" "}
                to enable password sign-in.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-background border-border shadow-md" data-testid="card-public-profile">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              {player.isPublicProfile ? (
                <Eye className="h-6 w-6 text-primary" />
              ) : (
                <EyeOff className="h-6 w-6 text-primary" />
              )}{" "}
              Privacy & Public Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="display-name">Display name (optional)</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={player.name}
                maxLength={120}
                data-testid="input-display-name"
              />
              <p className="text-xs text-muted-foreground">
                Shown on rosters and your public profile when &ldquo;Show my name&rdquo; is on.
                Leave blank to use your account name ({player.name}).
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={
                  updateProfile.isPending ||
                  (displayName.trim() || null) === (player.displayName ?? null)
                }
                onClick={() =>
                  updateProfile.mutate({
                    data: { displayName: displayName.trim() ? displayName.trim() : null },
                  })
                }
                data-testid="button-save-display-name"
              >
                Save display name
              </Button>
            </div>

            <div className="space-y-4 border-t border-border pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground">Show my name on rosters</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    When off, you appear as &ldquo;Anonymous Player&rdquo; on tournament rosters
                    and waitlists.
                  </p>
                </div>
                <Switch
                  checked={player.showName}
                  disabled={updateProfile.isPending}
                  onCheckedChange={(checked) =>
                    updateProfile.mutate({ data: { showName: checked } })
                  }
                  data-testid="switch-show-name"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground">Show my email on rosters</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Off by default. Other players can&apos;t see your email unless you turn this
                    on. Staff can always see it.
                  </p>
                </div>
                <Switch
                  checked={player.showEmail}
                  disabled={updateProfile.isPending}
                  onCheckedChange={(checked) =>
                    updateProfile.mutate({ data: { showEmail: checked } })
                  }
                  data-testid="switch-show-email"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground">Show my phone on rosters</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    Off by default. Useful for hosts coordinating rides or seat swaps.
                  </p>
                </div>
                <Switch
                  checked={player.showPhone}
                  disabled={updateProfile.isPending}
                  onCheckedChange={(checked) =>
                    updateProfile.mutate({ data: { showPhone: checked } })
                  }
                  data-testid="switch-show-phone"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground">Show my attendance heatmap</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    On by default. When off, the day-by-day heatmap is hidden on your public
                    profile. You&apos;ll still see your own heatmap here on this page.
                  </p>
                </div>
                <Switch
                  checked={player.publicAttendance}
                  disabled={updateProfile.isPending}
                  onCheckedChange={(checked) =>
                    updateProfile.mutate({ data: { publicAttendance: checked } })
                  }
                  data-testid="switch-public-attendance"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-border pt-4">
              <div>
                <p className="font-medium text-foreground">Public profile page</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  When on, anyone can view your season stats and recent finishes at{" "}
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    /players/{player.id}
                  </code>
                  . When off, the page returns 404.
                </p>
              </div>
              <Switch
                checked={player.isPublicProfile}
                disabled={updateProfile.isPending}
                onCheckedChange={(checked) =>
                  updateProfile.mutate({ data: { isPublicProfile: checked } })
                }
                data-testid="switch-public-profile"
              />
            </div>
            {player.isPublicProfile ? (
              <Link
                href={`/players/${player.id}`}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                data-testid="link-view-public-profile"
              >
                <ExternalLink className="h-4 w-4" /> View your public profile
              </Link>
            ) : null}
          </CardContent>
        </Card>

        <Card className="bg-background border-border shadow-md" data-testid="card-email-reminders">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              {player.remindersOptOut ? (
                <BellOff className="h-6 w-6 text-primary" />
              ) : (
                <Bell className="h-6 w-6 text-primary" />
              )}{" "}
              Email Reminders
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-medium text-foreground">Tournament reminders</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  When on, we email you 24 hours and 1 hour before each tournament you&apos;ve
                  RSVP&apos;d to. Turn off if you&apos;d rather not get the heads-up.
                </p>
              </div>
              <Switch
                checked={!player.remindersOptOut}
                disabled={updateProfile.isPending}
                onCheckedChange={(checked) =>
                  updateProfile.mutate({ data: { remindersOptOut: !checked } })
                }
                data-testid="switch-reminders"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-background border-border shadow-md" data-testid="card-upcoming-rsvps">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Calendar className="h-6 w-6 text-primary" /> Upcoming RSVPs
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {promotedBanners.length > 0 ? (
              <div className="space-y-2" data-testid="promoted-banners">
                {promotedBanners.map((rsvp) => (
                  <div
                    key={`promoted-${rsvp.tournamentId}`}
                    className="flex items-start gap-3 rounded-lg border border-green-500/40 bg-green-500/10 p-4"
                    data-testid={`banner-promoted-${rsvp.tournamentId}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground">You're confirmed!</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        A seat opened up and you've been promoted off the waitlist for{" "}
                        <Link
                          href={`/tournaments/${rsvp.tournamentId}`}
                          className="text-primary hover:underline font-medium"
                        >
                          {rsvp.tournamentName}
                        </Link>
                        . See you {formatDateTime(rsvp.date)}.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => dismissPromoted(rsvp.tournamentId)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      aria-label="Dismiss"
                      data-testid={`button-dismiss-promoted-${rsvp.tournamentId}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            {isUpcomingLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : !upcoming || upcoming.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">You haven't RSVP'd to any upcoming tournaments.</p>
                <Link href="/tournaments">
                  <Button className="mt-4 bg-primary text-primary-foreground">Browse Tournaments</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming.map((rsvp) => (
                  <div
                    key={rsvp.tournamentId}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-border bg-card/50 gap-3"
                    data-testid={`row-upcoming-${rsvp.tournamentId}`}
                  >
                    <div>
                      <Link
                        href={`/tournaments/${rsvp.tournamentId}`}
                        className="font-bold text-lg hover:text-primary transition-colors"
                      >
                        {rsvp.tournamentName}
                      </Link>
                      <div className="text-sm text-muted-foreground mt-1">
                        {formatDateTime(rsvp.date)} • {rsvp.leagueName}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-destructive/50 text-destructive hover:bg-destructive/10"
                      disabled={cancelMutation.isPending && cancelMutation.variables?.id === rsvp.tournamentId}
                      onClick={() => cancelMutation.mutate({ id: rsvp.tournamentId })}
                      data-testid={`button-cancel-${rsvp.tournamentId}`}
                    >
                      {cancelMutation.isPending && cancelMutation.variables?.id === rsvp.tournamentId ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <X className="mr-2 h-4 w-4" />
                      )}
                      Cancel RSVP
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-background border-border shadow-md">
          <CardHeader className="border-b border-border pb-6">
            <CardTitle className="text-2xl flex items-center gap-2">
              <History className="h-6 w-6 text-primary" /> Tournament History
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {isHistoryLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : !history || history.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-foreground">No tournaments yet</p>
                <p className="text-muted-foreground mt-2 mb-6">Hit the tables to start building your record.</p>
                <Link href="/tournaments">
                  <Button className="bg-primary text-primary-foreground">Find a Tournament</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((entry, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-border bg-card/50 hover:bg-card transition-colors gap-4">
                    <div>
                      <Link href={`/tournaments/${entry.tournamentId}`} className="font-bold text-lg hover:text-primary transition-colors">
                        {entry.tournamentName}
                      </Link>
                      <div className="text-sm text-muted-foreground mt-1">
                        {formatDateTime(entry.date)} • {entry.leagueName}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-8 sm:gap-12 pt-2 sm:pt-0 border-t border-border sm:border-0">
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pos</p>
                        <p className="font-bold text-foreground">
                          {entry.position > 0 ? (
                            <span className={entry.position <= 3 ? 'text-primary' : ''}>#{entry.position}</span>
                          ) : '-'}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pts</p>
                        <p className="font-bold text-primary">{entry.points || '-'}</p>
                      </div>
                      <div className="text-center w-24">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Won</p>
                        <p className="font-bold text-green-500">{entry.winnings > 0 ? formatMoney(entry.winnings) : '-'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
